import { useState, useEffect } from 'react';
import { Wifi, WifiOff, CloudOff, RefreshCw, Check } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { isOnline, getOfflineStatus, syncAllPendingData } from '../utils/offlineStorage';

const OfflineIndicator = () => {
  const { t } = useTranslation();
  const [online, setOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      toast({
        title: t('offline.backOnline', 'Back Online'),
        description: t('offline.connectionRestored', 'Your internet connection has been restored.'),
      });
      checkPendingData();
    };

    const handleOffline = () => {
      setOnline(false);
      setShowBanner(true);
      toast({
        title: t('offline.offline', 'You are offline'),
        description: t('offline.dataSavedLocally', 'Your data will be saved locally and synced when online.'),
        variant: 'destructive'
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    checkPendingData();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const checkPendingData = async () => {
    try {
      const status = await getOfflineStatus();
      setPendingCount(status.totalPending);
      if (status.totalPending > 0 && status.isOnline) {
        setShowBanner(true);
      }
    } catch (error) {
      console.error('Error checking offline status:', error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      // Import API service dynamically to avoid circular dependencies
      const { glucoseAPI, mealsAPI } = await import('../services/api');
      
      const result = await syncAllPendingData({
        glucose: { create: glucoseAPI.create },
        meals: { create: mealsAPI.create }
      });

      if (result.success) {
        const totalSynced = result.results.glucose.synced + result.results.meals.synced;
        toast({
          title: t('offline.syncComplete', 'Sync Complete'),
          description: `${totalSynced} ${t('offline.itemsSynced', 'items synced successfully')}`,
        });
        setPendingCount(0);
        setShowBanner(false);
      }
    } catch (error) {
      toast({
        title: t('common.error', 'Error'),
        description: t('offline.syncFailed', 'Failed to sync data. Please try again.'),
        variant: 'destructive'
      });
    }
    setSyncing(false);
  };

  // Don't show anything if online and no pending data
  if (online && pendingCount === 0 && !showBanner) {
    return null;
  }

  return (
    <>
      {/* Offline Banner */}
      {(!online || pendingCount > 0) && showBanner && (
        <div className={`fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 p-4 rounded-lg shadow-lg border ${
          online 
            ? 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800' 
            : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800'
        }`}>
          <div className="flex items-start gap-3">
            {online ? (
              <CloudOff className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            ) : (
              <WifiOff className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
            )}
            <div className="flex-1">
              <h4 className={`font-medium ${
                online 
                  ? 'text-yellow-800 dark:text-yellow-200' 
                  : 'text-red-800 dark:text-red-200'
              }`}>
                {online 
                  ? t('offline.pendingSync', 'Pending Data to Sync') 
                  : t('offline.offline', 'You are offline')
                }
              </h4>
              <p className={`text-sm mt-1 ${
                online 
                  ? 'text-yellow-700 dark:text-yellow-300' 
                  : 'text-red-700 dark:text-red-300'
              }`}>
                {online 
                  ? `${pendingCount} ${t('offline.itemsWaiting', 'items waiting to sync')}`
                  : t('offline.dataSavedLocally', 'Your data will be saved locally and synced when online.')
                }
              </p>
              {online && pendingCount > 0 && (
                <Button
                  size="sm"
                  onClick={handleSync}
                  disabled={syncing}
                  className="mt-2 bg-yellow-600 hover:bg-yellow-700 text-white"
                >
                  {syncing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      {t('offline.syncing', 'Syncing...')}
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {t('offline.syncNow', 'Sync Now')}
                    </>
                  )}
                </Button>
              )}
            </div>
            <button 
              onClick={() => setShowBanner(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Status Badge in Header (optional) */}
      {!online && (
        <Badge 
          variant="destructive" 
          className="fixed top-20 right-4 z-50 animate-pulse"
        >
          <WifiOff className="w-3 h-3 mr-1" />
          {t('offline.offline', 'Offline')}
        </Badge>
      )}
    </>
  );
};

export default OfflineIndicator;
